import { line } from './line';
import { area } from './area';
import { AnySeries } from '@d3fc/d3fc-series';
import { isValueWithInterpolation } from 'utils/interpolation';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import sortBy from 'lodash/sortBy';

export const posNegLine = <T>(key: keyof T & string, sign: 'pos' | 'neg') =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    line((d: any) => d[`${sign}-${key}`], `${key} pos-neg ${sign}`);

export const posNegArea = <T>(key: keyof T & string, sign: 'pos' | 'neg') =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    area((d: any) => d[`${sign}-${key}`], `${key} pos-neg ${sign}`);

export const posNegGradientLine: <T>(key: keyof T & string) => AnySeries[] = (
    key
) => [
    posNegLine(key, 'pos'),
    posNegLine(key, 'neg'),
    posNegArea(key, 'pos'),
    posNegArea(key, 'neg'),
];

export function splitPosNeg<T extends TimeSeriesChartDataItem>(
    data: T[],
    fieldToSplit: keyof T & string,
    fieldToKeep: keyof T & string
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const positiveNegativeData: any[] = [];
    const positiveKey = `pos-${fieldToSplit}`;
    const negativeKey = `neg-${fieldToSplit}`;

    let previousTime;
    let previousValue;
    let previousIsPositive = undefined;

    for (let i = 0; i < data.length; i++) {
        const d = data[i];
        const time = d.time;
        const value = getSectionValue(d, fieldToSplit);

        if (value === undefined) {
            positiveNegativeData.push({
                time,
                [positiveKey]: undefined,
                [negativeKey]: undefined,
                [fieldToKeep]: d[fieldToKeep],
            });
            previousIsPositive = undefined;
            continue;
        }

        if (value > 0) {
            positiveNegativeData.push({
                time,
                [positiveKey]: value,
                [negativeKey]: undefined,
                [fieldToKeep]: d[fieldToKeep],
            });
            if (
                previousTime !== undefined &&
                previousValue === 0 &&
                previousIsPositive === false
            ) {
                positiveNegativeData[positiveNegativeData.length - 2][
                    positiveKey
                ] = 0;
            }
            previousIsPositive = true;
        }

        if (value < 0) {
            positiveNegativeData.push({
                time,
                [positiveKey]: undefined,
                [negativeKey]: value,
                [fieldToKeep]: d[fieldToKeep],
            });
            if (
                previousTime !== undefined &&
                previousValue === 0 &&
                previousIsPositive === true
            ) {
                positiveNegativeData[positiveNegativeData.length - 2][
                    negativeKey
                ] = 0;
            }
            previousIsPositive = false;
        }

        if (value === 0) {
            if (previousIsPositive) {
                positiveNegativeData.push({
                    time,
                    [positiveKey]: 0,
                    [negativeKey]: undefined,
                    [fieldToKeep]: d[fieldToKeep],
                });
                previousIsPositive = true;
            } else {
                positiveNegativeData.push({
                    time,
                    [positiveKey]: undefined,
                    [negativeKey]: 0,
                    [fieldToKeep]: d[fieldToKeep],
                });
                previousIsPositive = false;
            }
        }

        if (
            previousTime !== undefined &&
            previousValue !== undefined &&
            previousValue * value < 0
        ) {
            const alpha = (value - previousValue) / (time - previousTime);
            const interpolatedTime = previousTime - previousValue / alpha;
            positiveNegativeData.push({
                time: interpolatedTime,
                [positiveKey]: 0,
                [negativeKey]: 0,
                [fieldToKeep]:
                    positiveNegativeData[positiveNegativeData.length - 1][
                        fieldToKeep
                    ],
            });
            previousIsPositive = value > 0;
        }

        previousTime = time;
        previousValue = value;
    }

    return positiveNegativeData;
}

function getSectionValue<T>(d: T, field: keyof T) {
    const fieldValue = d[field];
    return fieldValue && isValueWithInterpolation(fieldValue)
        ? fieldValue.value
        : (fieldValue as unknown as number);
}

class ItemWithPosNegPropsWrapper<T> {
    public constructor(
        private item: T,
        private key: keyof T & string
    ) {}

    public getPositiveValue() {
        return this.getPosNegValue('pos');
    }

    public getNegativeValue() {
        return this.getPosNegValue('neg');
    }

    public getValue() {
        return this.getValueForKey(this.key);
    }

    private getPosNegValue(from: 'pos' | 'neg') {
        const posNegKey = `${from}-${this.key}`;
        return this.getValueForKey(posNegKey);
    }

    private getValueForKey(key: string | keyof T) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this.item as any)[key];
    }
}

export function fillPosNegGaps<T extends { time: number }>(
    data: T[],
    key: keyof T & string,
    interpolateKeys: Array<keyof T>
): T[] {
    const positiveKey = `pos-${key}`;
    const negativeKey = `neg-${key}`;

    const wrap = (item: T) => new ItemWithPosNegPropsWrapper<T>(item, key);
    function isPositive(item: T): boolean {
        const posValue = wrap(item).getPositiveValue();
        return posValue !== undefined;
    }
    function isNegative(item: T): boolean {
        const negValue = wrap(item).getNegativeValue();
        return negValue !== undefined;
    }
    function changingSign(x: T, y: T) {
        return (
            (isPositive(x) && isNegative(y)) || (isPositive(y) && isNegative(x))
        );
    }

    let prevWithValue: T | undefined = undefined;

    const itemsToAdd: T[] = [];

    for (let i = 0; i < data.length; i++) {
        const current = data[i];

        if (prevWithValue && changingSign(current, prevWithValue)) {
            const midTime =
                current.time - (current.time - prevWithValue.time) / 2;
            const itemToAdd = {
                ...prevWithValue,
                time: midTime,
                [key]: 0,
                [positiveKey]: 0,
                [negativeKey]: 0,
            };
            itemsToAdd.push(itemToAdd);
            for (const key of interpolateKeys) {
                const before = data.filter((d) => d.time < midTime);
                if (before.length > 0) {
                    const prev = before[before.length - 1];
                    const prevValue = prev[key] as unknown as number;
                    const currentValue = current[key] as unknown as number;
                    itemToAdd[key] = (prevValue +
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (currentValue - prevValue) / 2) as any;
                }
            }
        }

        if (isPositive(current) || isNegative(current)) {
            prevWithValue = current;
        }
    }

    return sortBy([...data, ...itemsToAdd], 'time');
}
